<template>
	<table class="ddd-table">
		<thead>
			<!-- Row 0: Date headers -->
			<tr class="ddd-thead-dates">
				<th class="ddd-label-head"></th>
				<th v-for="day in props.days" :key="day.id" :style="getDayHeaderStyle(day.status)" :title="t('planning_duty_status_' + day.status)">
					<div class="ddd-day-head">
						<span class="ddd-day-wd">{{ weekdayShort(day.date) }}</span>
						<span class="ddd-day-dt">{{ formatDayDate(day.date) }}</span>
					</div>
				</th>
			</tr>

			<!-- Row 1: Duty names -->
			<tr class="ddd-thead-duty">
				<th class="ddd-label-head ddd-label-head--duty">{{ t('duty_duty') }}</th>
				<th
					v-for="(day, idx) in props.driverDays"
					:key="'duty-' + idx"
					:style="getDutyCellStyleForDay(day)"
					:title="dutyTooltip(day.duty)"
					class="ddd-cell-center"
				>
					<span v-if="day.duty" class="ddd-duty-badge">{{ day.duty.name }}</span>
				</th>
			</tr>
		</thead>

		<tbody>
			<!-- Shift times -->
			<tr>
				<td class="ddd-label">{{ t('duty_shift') }}</td>
				<td
					v-for="(day, idx) in props.driverDays"
					:key="'shift-' + idx"
					:style="getShiftCellStyle(day)"
					:title="day.shift !== undefined ? t('duty_shift_' + day.shift) : ''"
				>
					{{ shiftTimeText(day) }}
				</td>
			</tr>

			<!-- Standby -->
			<tr>
				<td class="ddd-label">{{ t('planning_driver_debug_standby') }}</td>
				<td v-for="(day, idx) in props.driverDays" :key="'standby-' + idx" :style="getStandbyCellStyle(day)">
					{{ standbyTimeText(day) }}
				</td>
			</tr>

			<!-- Rest before duty -->
			<tr class="ddd-row-group">
				<td class="ddd-label">{{ t('planning_driver_debug_restBeforeDuty') }}</td>
				<td v-for="(day, idx) in props.driverDays" :key="'rbd-' + idx">
					{{ day.restBeforeDuty !== undefined ? safeFormatSecondsAsTime(day.restBeforeDuty) : '' }}
				</td>
			</tr>

			<!-- Work time -->
			<tr class="ddd-row-group">
				<td class="ddd-label">{{ t('planning_driver_debug_workTime') }}</td>
				<td v-for="(day, idx) in props.driverDays" :key="'wt-' + idx">
					{{ workTimeText(day) }}
				</td>
			</tr>

			<!-- Overtime -->
			<tr>
				<td class="ddd-label">{{ t('planning_driver_debug_overtime') }}</td>
				<td v-for="(day, idx) in props.driverDays" :key="'ot-' + idx">
					{{ overtimeText(day) }}
				</td>
			</tr>

			<!-- Manual corrections -->
			<tr>
				<td class="ddd-label">{{ t('user_edit_manual_corrections') }}</td>
				<td v-for="(day, idx) in props.driverDays" :key="'mc-' + idx">
					{{ safeFormatSecondsAsTime(getManualAmount(day.statistics?.day)) }}
				</td>
			</tr>

			<!-- Rest day balance -->
			<tr class="ddd-row-group">
				<td class="ddd-label">{{ t('planning_driver_debug_restDayBalance') }}</td>
				<td v-for="(day, idx) in props.driverDays" :key="'rdb-' + idx">
					{{ restDayBalanceText(day) }}
				</td>
			</tr>

			<!-- Vacation -->
			<tr>
				<td class="ddd-label">{{ t('planning_driver_debug_vacation') }}</td>
				<td v-for="(day, idx) in props.driverDays" :key="'vac-' + idx" class="ddd-cell-wrap">
					{{ vacationText(day) }}
				</td>
			</tr>

			<!-- Breaks -->
			<tr class="ddd-row-group">
				<td class="ddd-label">{{ t('breaks') }}</td>
				<td v-for="(day, idx) in props.driverDays" :key="'brk-' + idx" class="ddd-cell-wrap">
					<template v-if="day.duty?.breaks?.length">
						<div v-for="(b, bi) in day.duty.breaks" :key="bi" class="ddd-break-line">
							{{ safeFormatSecondsAsTime(b.start) }} – {{ safeFormatSecondsAsTime(b.end) }}
						</div>
						<span class="ddd-break-total">{{ t('planning_breaks_length') }}{{ safeFormatSecondsAsTime(day.duty.breakTime) }}</span>
					</template>
				</td>
			</tr>

			<!-- Basic fitness -->
			<tr>
				<td class="ddd-label">{{ t('basic_fitness') }}</td>
				<td v-for="(day, idx) in props.driverDays" :key="'bf-' + idx">
					{{
						day.statistics?.day !== undefined && props.basicFitnessData[day.statistics.day] !== undefined
							? props.basicFitnessData[day.statistics.day]
							: '–'
					}}
				</td>
			</tr>

			<!-- Met -->
			<tr class="ddd-row-group">
				<td class="ddd-label ddd-label--met">{{ t('duty_met') }}</td>
				<td v-for="(m, i) in props.metDays" :key="'met-' + i" :style="getMetCellStyle(m)" :title="m.title">
					<template v-if="m.total > 0">
						<span class="ddd-stat ddd-stat--met">{{ formatToTwoDecimals(m.total) }}</span>
						<span class="ddd-stat-meta">
							({{ t('planning_driver_debug_count') }} {{ m.count }}, {{ t('planning_driver_debug_total') }} {{ formatToTwoDecimals(m.sum) }})
						</span>
					</template>
				</td>
			</tr>

			<!-- Unmet -->
			<tr>
				<td class="ddd-label ddd-label--unmet">{{ t('duty_unmet') }}</td>
				<td v-for="(u, i) in props.unmetDays" :key="'unmet-' + i" :style="getUnmetCellStyle(u)" :title="u.title">
					<template v-if="u.total > 0">
						<span class="ddd-stat ddd-stat--unmet">{{ formatToTwoDecimals(u.total) }}</span>
						<span class="ddd-stat-meta">
							({{ t('planning_driver_debug_count') }} {{ u.count }}, {{ t('planning_driver_debug_total') }} {{ formatToTwoDecimals(u.sum) }})
						</span>
					</template>
				</td>
			</tr>

			<!-- Violations -->
			<tr>
				<td class="ddd-label ddd-label--viol">{{ t('duty_violations') }}</td>
				<td v-for="(v, i) in props.violationsDays" :key="'viol-' + i" :style="getViolCellStyle(v)" :title="v.title">
					<template v-if="v.total > 0">
						<span class="ddd-stat ddd-stat--viol">{{ formatToTwoDecimals(v.total) }}</span>
						<span class="ddd-stat-meta">({{ t('planning_driver_debug_count') }} {{ v.count }})</span>
					</template>
				</td>
			</tr>

			<!-- Pensum -->
			<tr class="ddd-row-group">
				<td class="ddd-label">{{ t('planning_driver_debug_pensum') }}</td>
				<td v-for="(day, idx) in props.driverDays" :key="'pen-' + idx">
					{{ day.pensumMultiplier != null ? day.pensumMultiplier * 100 + '%' : '' }}
				</td>
			</tr>

			<!-- ── Extra pay ─────────────────────────────────────────── -->
			<tr class="ddd-row-group">
				<td class="ddd-label">{{ t('planning_extraPayNight1') }}</td>
				<td v-for="(day, idx) in props.driverDays" :key="'epn1-' + idx">{{ day.statistics?.extraPayNight1 ?? '' }}</td>
			</tr>
			<tr>
				<td class="ddd-label">{{ t('planning_extraPayNight2') }}</td>
				<td v-for="(day, idx) in props.driverDays" :key="'epn2-' + idx">{{ day.statistics?.extraPayNight2 ?? '' }}</td>
			</tr>
			<tr>
				<td class="ddd-label">{{ t('planning_extraPaySaturday') }}</td>
				<td v-for="(day, idx) in props.driverDays" :key="'epsat-' + idx">{{ day.statistics?.extraPaySaturday ?? '' }}</td>
			</tr>
			<tr>
				<td class="ddd-label">{{ t('planning_extraPaySunday') }}</td>
				<td v-for="(day, idx) in props.driverDays" :key="'epsun-' + idx">{{ day.statistics?.extraPaySunday ?? '' }}</td>
			</tr>
			<tr>
				<td class="ddd-label">{{ t('planning_customExtraPay') }}</td>
				<td v-for="(day, idx) in props.driverDays" :key="'cep-' + idx" class="ddd-cell-wrap">
					{{ day.statistics?.customExtraPay && Object.keys(day.statistics.customExtraPay).length ? day.statistics.customExtraPay : '' }}
				</td>
			</tr>
			<tr>
				<td class="ddd-label ddd-label--total">{{ t('planning_driver_debug_extraPaySum') }}</td>
				<td v-for="(day, idx) in props.driverDays" :key="'eps-' + idx" class="ddd-cell-total">
					{{ day.statistics?.extraPaySum ?? '' }}
				</td>
			</tr>
			<tr>
				<td class="ddd-label ddd-label--total">{{ t('planning_driver_debug_monthlyExtraPaySum') }}</td>
				<td v-for="(day, idx) in props.driverDays" :key="'meps-' + idx" class="ddd-cell-total">
					{{ day.statistics?.monthlyExtraPaySum ?? '' }}
				</td>
			</tr>

			<!-- ── Time allowance ────────────────────────────────────── -->
			<tr class="ddd-row-group">
				<td class="ddd-label">{{ t('planning_timeAllowanceNight1') }}</td>
				<td v-for="(day, idx) in props.driverDays" :key="'tan1-' + idx">
					{{ day.statistics?.timeAllowanceNight1 !== undefined ? safeFormatSecondsAsTime(day.statistics.timeAllowanceNight1) : '' }}
				</td>
			</tr>
			<tr>
				<td class="ddd-label">{{ t('planning_timeAllowanceNight2a') }}</td>
				<td v-for="(day, idx) in props.driverDays" :key="'tan2a-' + idx">
					{{ day.statistics?.timeAllowanceNight2a !== undefined ? safeFormatSecondsAsTime(day.statistics.timeAllowanceNight2a) : '' }}
				</td>
			</tr>
			<tr>
				<td class="ddd-label">{{ t('planning_timeAllowanceNight2b') }}</td>
				<td v-for="(day, idx) in props.driverDays" :key="'tan2b-' + idx">
					{{ day.statistics?.timeAllowanceNight2b !== undefined ? safeFormatSecondsAsTime(day.statistics.timeAllowanceNight2b) : '' }}
				</td>
			</tr>
			<tr>
				<td class="ddd-label">{{ t('planning_timeAllowanceBreak1') }}</td>
				<td v-for="(day, idx) in props.driverDays" :key="'tab1-' + idx">
					{{ day.statistics?.timeAllowanceBreak1 !== undefined ? safeFormatSecondsAsTime(day.statistics.timeAllowanceBreak1) : '' }}
				</td>
			</tr>
			<tr>
				<td class="ddd-label">{{ t('planning_timeAllowanceBreak2') }}</td>
				<td v-for="(day, idx) in props.driverDays" :key="'tab2-' + idx">
					{{ day.statistics?.timeAllowanceBreak2 !== undefined ? safeFormatSecondsAsTime(day.statistics.timeAllowanceBreak2) : '' }}
				</td>
			</tr>
			<tr>
				<td class="ddd-label ddd-label--total">{{ t('planning_driver_debug_timeAllowanceSum') }}</td>
				<td v-for="(day, idx) in props.driverDays" :key="'tas-' + idx" class="ddd-cell-total">
					{{ day.statistics?.timeAllowanceSum !== undefined ? safeFormatSecondsAsTime(day.statistics.timeAllowanceSum) : '' }}
				</td>
			</tr>
			<tr>
				<td class="ddd-label">{{ t('planning_driver_debug_timeAllowanceCompensation') }}</td>
				<td v-for="(day, idx) in props.driverDays" :key="'tac-' + idx">
					{{ day.statistics?.timeAllowanceCompensation !== undefined ? safeFormatSecondsAsTime(day.statistics.timeAllowanceCompensation) : '' }}
				</td>
			</tr>
			<tr>
				<td class="ddd-label ddd-label--total">{{ t('planning_driver_debug_monthlyTimeAllowanceSum') }}</td>
				<td v-for="(day, idx) in props.driverDays" :key="'mtas-' + idx" class="ddd-cell-total">
					{{ day.statistics?.monthlyTimeAllowanceSum !== undefined ? safeFormatSecondsAsTime(day.statistics.monthlyTimeAllowanceSum) : '' }}
				</td>
			</tr>
			<tr>
				<td class="ddd-label">{{ t('planning_driver_debug_timeAllowanceBalance') }}</td>
				<td v-for="(day, idx) in props.driverDays" :key="'tab-' + idx">
					{{ day.statistics?.timeAllowanceBalance !== undefined ? safeFormatSecondsAsTime(day.statistics.timeAllowanceBalance) : '' }}
				</td>
			</tr>
			<tr>
				<td class="ddd-label">{{ t('planning_driver_debug_currentExtraWork') }}</td>
				<td v-for="(day, idx) in props.driverDays" :key="'cew-' + idx">
					{{ day.statistics?.currentExtraWork !== undefined ? safeFormatSecondsAsTime(day.statistics.currentExtraWork) : '' }}
				</td>
			</tr>
			<tr>
				<td class="ddd-label">{{ t('planning_driver_debug_extraWorkCompensation') }}</td>
				<td v-for="(day, idx) in props.driverDays" :key="'ewc-' + idx">
					{{ day.statistics?.extraWorkCompensation !== undefined ? safeFormatSecondsAsTime(day.statistics.extraWorkCompensation) : '' }}
				</td>
			</tr>
			<tr>
				<td class="ddd-label">{{ t('planning_driver_debug_extraWorkBalance') }}</td>
				<td v-for="(day, idx) in props.driverDays" :key="'ewb-' + idx">
					{{ day.statistics?.extraWorkBalance !== undefined ? safeFormatSecondsAsTime(day.statistics.extraWorkBalance) : '' }}
				</td>
			</tr>

			<!-- ── IDs ───────────────────────────────────────────────── -->
			<tr>
				<td class="ddd-label">{{ t('planning_driver_debug_dayId') }}</td>
				<td v-for="day in props.days" :key="'did-' + day.id">{{ day.id }}</td>
			</tr>
			<tr>
				<td class="ddd-label">{{ t('planning_driver_debug_dutyId') }}</td>
				<td v-for="(day, idx) in props.driverDays" :key="'duid-' + idx">{{ day.duty?.id ?? '' }}</td>
			</tr>

			<!-- ── Score section ─────────────────────────────────────── -->
			<tr class="ddd-section-header">
				<th class="ddd-label ddd-section-sticky">{{ t('planning_driver_debug_score') }}</th>
				<th :colspan="props.days.length"></th>
			</tr>
			<tr v-for="key in props.scoreKeys" :key="'score-' + key">
				<td class="ddd-label">{{ key }}</td>
				<td v-for="(day, idx) in props.driverDays" :key="'sv-' + idx">
					{{ formatToTwoDecimals(day.score?.[key]) }}
				</td>
			</tr>

			<!-- ── Fitness data section ───────────────────────────────── -->
			<tr class="ddd-section-header">
				<th class="ddd-label ddd-section-sticky">{{ t('planning_driver_debug_fitnessData') }}</th>
				<th :colspan="props.days.length"></th>
			</tr>
			<tr v-for="key in props.fitnessDataKeys" :key="'fit-' + key">
				<td class="ddd-label">{{ key }}</td>
				<td v-for="(data, idx) in props.fitnessData" :key="'fv-' + idx" class="ddd-cell-wrap">
					{{ renderFitnessCell(data, key) }}
				</td>
				<td v-for="i in Math.max(0, props.days.length - props.fitnessData.length)" :key="'fpad-' + i"></td>
			</tr>
		</tbody>
	</table>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { ApiAnnotationDriverDay, ApiRosterDay, ApiFitnessData, DayEvaluation } from '@/rennovation/planning-view/types/api/roster-response';
import { useDriverDebugFormatters } from '@/rennovation/planning-view/composables/useDriverDebugFormatters';
import { useDriverDebugStyles } from '@/rennovation/planning-view/composables/useDriverDebugStyles';
import { useDriverDebugCellHelpers } from '@/rennovation/planning-view/composables/useDriverDebugCellHelpers';
import { safeFormatSecondsAsTime, formatToTwoDecimals, weekdayShort, formatDayDate } from '@/rennovation/shared/utils/date-utils';

// ─────────────────────────────────────────────────────────────────────────────
// Props
// ─────────────────────────────────────────────────────────────────────────────
const props = defineProps<{
	days: ApiRosterDay[];
	driverDays: ApiAnnotationDriverDay[];
	scoreKeys: string[];
	fitnessDataKeys: string[];
	fitnessData: ApiFitnessData[];
	basicFitnessData: Record<number, number | null>;
	metDays: DayEvaluation[];
	unmetDays: DayEvaluation[];
	violationsDays: DayEvaluation[];
	manualWorktimeBalances: Array<{ day: { id: string }; amount: number }>;
}>();

// ─────────────────────────────────────────────────────────────────────────────
// i18n
// ─────────────────────────────────────────────────────────────────────────────
const { t } = useI18n();

const { shiftTimeText, standbyTimeText, workTimeText, overtimeText, restDayBalanceText, vacationText, dutyTooltip } = useDriverDebugFormatters();

const { getDayHeaderStyle, getDutyCellStyleForDay, getShiftCellStyle, getStandbyCellStyle, getMetCellStyle, getUnmetCellStyle, getViolCellStyle } =
	useDriverDebugStyles();

const { getManualAmount, renderFitnessCell } = useDriverDebugCellHelpers(computed(() => props.manualWorktimeBalances));
</script>

<style scoped>
/* ── Table base ──────────────────────────────────────────────────────────── */
.ddd-table {
	border-collapse: collapse;
	min-width: max-content;
	font-family: var(--ddd-font-ui);
	font-size: 13px;
	color: var(--ddd-text-primary);
}

.ddd-table th,
.ddd-table td {
	padding: 6px 11px;
	min-width: var(--ddd-cell-min);
	max-width: 210px;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	border-right: 1px solid var(--ddd-border-row);
	border-bottom: 1px solid var(--ddd-border-row);
	vertical-align: middle;
	color: var(--ddd-text-primary);
	font-family: var(--ddd-font-ui);
	font-weight: 400;
}

/* ── Sticky: Row 0 — date headers ────────────────────────────────────────── */
/*
 * NO background property here — each <th> receives its color exclusively via
 * the inline :style binding. Any CSS background here would fight inline styles.
 */
.ddd-thead-dates th {
	position: sticky;
	top: 0;
	z-index: 4;
	border-bottom: 1px solid var(--ddd-border);
	font-family: var(--ddd-font-ui);
	font-size: 11px;
	color: var(--ddd-text-primary);
}

/* ── Sticky: Row 1 — duty names ──────────────────────────────────────────── */
.ddd-thead-duty th {
	position: sticky;
	top: var(--ddd-row0-h);
	z-index: 3;
	border-bottom: 1px solid var(--ddd-border-row);
	box-shadow: inset 0 -1px 0 var(--ddd-border-row);
	font-family: var(--ddd-font-ui);
	font-size: 12px;
	color: var(--ddd-text-primary);
}

/* ── Sticky: first column (label) ────────────────────────────────────────── */
.ddd-label,
.ddd-label-head {
	position: sticky !important;
	left: 0;
	z-index: 2;
	background: var(--ddd-bg-card);
	min-width: var(--ddd-label-w) !important;
	max-width: var(--ddd-label-w) !important;
	white-space: normal !important;
	word-break: break-word;
	font-family: var(--ddd-font-ui) !important;
	font-size: 12.5px !important;
	font-weight: 500;
	color: var(--ddd-text-primary) !important;
	border-right: none !important;
	box-shadow: inset -1px 0 0 var(--ddd-sticky-shadow);
	line-height: 1.4;
}

.ddd-thead-dates .ddd-label-head {
	z-index: 5 !important;
	background: var(--ddd-bg-card) !important;
}

.ddd-thead-duty .ddd-label-head--duty {
	z-index: 4 !important;
	background: var(--ddd-bg-card) !important;
}

/* ── Day head cell ───────────────────────────────────────────────────────── */
.ddd-day-head {
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 2px;
}

.ddd-day-wd {
	font-size: 10px;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.06em;
	color: var(--ddd-text-primary);
	font-family: var(--ddd-font-ui);
}

.ddd-day-dt {
	font-size: 12.5px;
	font-weight: 800;
	color: var(--ddd-text-primary);
	font-family: var(--ddd-font-ui);
}

/* ── Duty badge ──────────────────────────────────────────────────────────── */
.ddd-duty-badge {
	display: inline-block;
	padding: 2px 8px;
	border-radius: 4px;
	font-size: 12px;
	font-weight: 700;
	letter-spacing: 0.02em;
	font-family: var(--ddd-font-ui);
	color: var(--ddd-text-primary);
}

/* ── Row separators ──────────────────────────────────────────────────────── */
.ddd-row-group td,
.ddd-row-group th {
	border-top: 2px solid var(--ddd-border) !important;
}

/* ── Section header (Score / Fitness Data) ───────────────────────────────── */
.ddd-section-header th {
	background: var(--ddd-bg-section);
	font-family: var(--ddd-font-ui);
	font-size: 10.5px;
	font-weight: 700;
	text-align: left;
	color: var(--ddd-text-primary);
	text-transform: uppercase;
	letter-spacing: 0.08em;
	padding: 5px 11px;
	border-top: 2px solid var(--ddd-border);
}

/* ── Score / fitness rows ────────────────────────────────────────────────── */
.ddd-table tbody tr td {
	background-color: transparent;
}

.ddd-table tbody tr td.ddd-label {
	background-color: var(--ddd-bg-card);
}

/* ── Label variants ──────────────────────────────────────────────────────── */
.ddd-label--total {
	color: var(--ddd-text-dark) !important;
	font-weight: 700 !important;
}

.ddd-label--met {
	color: var(--ddd-color-met) !important;
}

.ddd-label--unmet {
	color: var(--ddd-color-unmet) !important;
}

.ddd-label--viol {
	color: var(--ddd-color-viol) !important;
}

/* ── Cell variants ───────────────────────────────────────────────────────── */
.ddd-cell-wrap {
	white-space: normal !important;
	word-break: break-word;
	overflow: visible !important;
	text-overflow: unset !important;
	max-width: 230px !important;
	line-height: 1.5;
	color: var(--ddd-text-primary);
}

.ddd-cell-total {
	font-weight: 700;
	color: var(--ddd-text-dark);
	background: var(--ddd-bg-total-cell) !important;
}

.ddd-cell-center {
	text-align: center;
}

/* ── Stats ───────────────────────────────────────────────────────────────── */
.ddd-stat {
	font-weight: 700;
	font-size: 12.5px;
}

.ddd-stat--met {
	color: var(--ddd-color-met);
}

.ddd-stat--unmet {
	color: var(--ddd-color-unmet);
}

.ddd-stat--viol {
	color: var(--ddd-color-viol);
}

.ddd-stat-meta {
	font-size: 11px;
	color: var(--ddd-text-primary);
	margin-left: 3px;
}

/* ── Breaks ──────────────────────────────────────────────────────────────── */
.ddd-break-line {
	font-size: 12px;
	line-height: 1.6;
	color: var(--ddd-text-primary);
}

.ddd-break-total {
	font-size: 11px;
	color: var(--ddd-text-primary);
	display: block;
	margin-top: 2px;
}
</style>
